import React, { useState, useRef, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Divider,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { X, Trash2 } from "lucide-react";
import { Sparkles } from "@mynaui/icons-react";
import { useAuth } from "../../shared/context/AuthContext";
import agentService from "../../services/agentService";

function ChatBotModal({ open, onClose }) {
  const { setActiveChatSession } = useAuth();

  // Both populated from backend — never generated on frontend
  const sessionId = useRef(null);
  const memoryId = useRef(null);
  const typewriterRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);

  const messagesEndRef = useRef(null);

  // ── Typewriter effect ───────────────────────────────────────────────────────
  const startTypewriter = (fullText, thinkTime) => {
    setMessages((prev) => [
      ...prev,
      { role: "bot", thinkTime, text: "", typing: true },
    ]);

    let i = 0;
    const tick = () => {
      i += 3;
      const displayed = fullText.slice(0, i);
      const done = i >= fullText.length;
      setMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last?.typing) {
          updated[updated.length - 1] = {
            ...last,
            text: displayed,
            typing: !done,
          };
        }
        return updated;
      });
      if (!done) {
        typewriterRef.current = setTimeout(tick, 16);
      }
    };
    typewriterRef.current = setTimeout(tick, 16);
  };

  // ── Load history from backend on open ──────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    clearTimeout(typewriterRef.current);
    setInput("");
    setThinking(false);
    setLoading(true);

    agentService
      .initChat()
      .then((res) => {
        const data = res.data?.data || res.data;

        sessionId.current = data.sessionId;
        memoryId.current = data.memoryId;

        setActiveChatSession({
          sessionId: data.sessionId,
          memoryId: data.memoryId,
        });

        const restored = (data.messages || []).map((m) => ({
          role: m.role,
          text: m.message,
          thinkTime: m.thinkTime,
          typing: false,
        }));
        setMessages(restored);
      })
      .catch((err) => {
        console.error("Failed to init chat:", err);
        setMessages([]);
      })
      .finally(() => setLoading(false));
  }, [open, setActiveChatSession]);

  // ── Auto scroll ─────────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, thinking]);

  // ── Call backend ────────────────────────────────────────────────────────────
const askAgent = async (question) => {
  setThinking(true);
  const startTime = Date.now();

  try {
    // Submit question — returns requestId immediately
    const res       = await agentService.chat(question, sessionId.current);
    const data      = res.data?.data || res.data;
    const requestId = data?.requestId;

    if (!requestId) throw new Error('No requestId returned');

    // Poll until bot message is ready
    const answer    = await pollForResponse(requestId);
    const thinkTime = Math.round((Date.now() - startTime) / 1000);

    startTypewriter(answer, Math.max(thinkTime, 1));

  } catch (err) {
    console.error("Agent error:", err);
    setMessages((prev) => [
      ...prev,
      {
        role:      "bot",
        thinkTime: 1,
        text:      "Sorry, something went wrong. Please try again.",
        typing:    false
      },
    ]);
  } finally {
    setThinking(false);
  }
};

// ── Poll for response ────────────────────────────────────────────────────────
const pollForResponse = async (requestId) => {
  const maxWaitMs  = 120000; 
  const intervalMs = 2000;   
  const startTime  = Date.now();

  while (Date.now() - startTime < maxWaitMs) {
    await new Promise((r) => setTimeout(r, intervalMs));

    const res  = await agentService.pollStatus(requestId);
    const data = res.data?.data || res.data;

    if (data.status === 'complete')   return data.response;
    if (data.status === 'error')      throw new Error(data.error || 'Agent error');
    if (data.status === 'not_found')  throw new Error('Request not found');
    
  }

  throw new Error('Response timed out after 2 minutes');
};

  // ── Send handler ────────────────────────────────────────────────────────────
  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || thinking || !sessionId.current) return;
    setMessages((prev) => [...prev, { role: "user", text: trimmed }]);
    setInput("");
    await askAgent(trimmed);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Clear memory ────────────────────────────────────────────────────────────
  const handleClearMemory = async () => {
    if (clearing || !sessionId.current) return;
    setClearing(true);
    clearTimeout(typewriterRef.current);

    try {
      const res = await agentService.clearMemory(sessionId.current);
      const data = res.data?.data || res.data;

      sessionId.current = data.sessionId;
      memoryId.current = data.memoryId;

      setActiveChatSession({
        sessionId: data.sessionId,
        memoryId: data.memoryId,
      });

      setMessages([]);
    } catch (err) {
      console.error("Failed to clear memory:", err);
    } finally {
      setClearing(false);
    }
  };

  // ── UI ───────────────────────────────────────────────────────────────────────
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: 480,
            maxWidth: "100vw",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "-4px 0px 24px rgba(0, 0, 0, 0.1)",
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
        }}
      >
        <Typography
          sx={{ fontWeight: 700, fontSize: "18px", color: "#020618" }}
        >
          Chat with Agent
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Clear memory">
            <IconButton
              onClick={handleClearMemory}
              disabled={clearing}
              size="small"
              sx={{ color: "#9CA3AF", "&:hover": { color: "#EF4444" } }}
            >
              {clearing ? <CircularProgress size={16} /> : <Trash2 size={16} />}
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small">
            <X size={20} color="#6B7280" />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ borderColor: "#E2E8F0" }} />

      {/* Messages */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          px: 3,
          py: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Loading state */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress size={24} sx={{ color: "#9CA3AF" }} />
          </Box>
        )}

        {/* Empty state */}
        {!loading && messages.length === 0 && !thinking && (
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Sparkles size={32} color="#9CA3AF" />
            <Typography sx={{ fontSize: "14px", color: "#6B7280", mt: 1 }}>
              Ask me anything about your assets,
              <br /> sensors, work orders or plant data.
            </Typography>
          </Box>
        )}

        {/* Messages list */}
        {!loading &&
          messages.map((msg, idx) =>
            msg.role === "user" ? (
              <Box key={idx} sx={{ alignSelf: "flex-end", maxWidth: "80%" }}>
                <Box
                  sx={{
                    bgcolor: "#F3F4F6",
                    borderRadius: "12px",
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Typography
                    sx={{ fontSize: "14px", color: "#020618", lineHeight: 1.5 }}
                  >
                    {msg.text}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box key={idx} sx={{ alignSelf: "flex-start", maxWidth: "85%" }}>
                {msg.thinkTime && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mb: 1,
                    }}
                  >
                    <Sparkles size={14} color="#6B7280" />
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#6B7280",
                        fontWeight: 500,
                      }}
                    >
                      Thought for {msg.thinkTime} seconds
                    </Typography>
                  </Box>
                )}
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#020618",
                    lineHeight: 1.6,
                    whiteSpace: "pre-line",
                  }}
                >
                  {msg.text}
                  {msg.typing && (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        width: "2px",
                        height: "1em",
                        bgcolor: "#020618",
                        ml: "1px",
                        verticalAlign: "text-bottom",
                        "@keyframes blink": {
                          "0%, 100%": { opacity: 1 },
                          "50%": { opacity: 0 },
                        },
                        animation: "blink 0.8s step-end infinite",
                      }}
                    />
                  )}
                </Typography>
              </Box>
            ),
          )}

        {/* Thinking indicator */}
        {thinking && (
          <Box sx={{ alignSelf: "flex-start" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Sparkles size={14} color="#6B7280" />
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#6B7280",
                  fontWeight: 500,
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.4 },
                  },
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              >
                Thinking...
              </Typography>
            </Box>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input area */}
      <Box sx={{ px: 2.5, pb: 2.5, pt: 1.5 }}>
        <Box
          sx={{
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={3}
            placeholder="Ask a question ..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            variant="standard"
            slotProps={{ input: { disableUnderline: true } }}
            sx={{
              px: 2,
              pt: 1.5,
              pb: 0.5,
              "& .MuiInputBase-input": {
                fontSize: "14px",
                color: "#020618",
                "&::placeholder": { color: "#9CA3AF", opacity: 1 },
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end", // ← changed from space-between
              px: 1.5,
              pb: 1.5,
            }}
          >
            <Box
              onClick={handleSend}
              sx={{
                bgcolor: "#399432",
                color: "#fff",
                borderRadius: "8px",
                px: 2,
                py: 0.75,
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                "&:hover": { bgcolor: "#2E7628" },
                opacity: input.trim() && !thinking ? 1 : 0.6,
              }}
            >
              Send
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}

export default ChatBotModal;
